import type {
  BucketBalances,
  PlayerOperationsResponse,
} from "@pax/api-contract/accounting/v1alpha1";
import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { creditsFromRaw } from "@/features/credits/format";
import { Tabs } from "@/features/evals/components";
import { Problem } from "@/features/staff/components";
import {
  type BalanceChange,
  balanceChange,
  balanceSteps,
  type HistoryWindow,
  heldBuckets,
  SPEND_ORDER,
  type SpendBy,
  spendable,
} from "./balances";
import { StackedSteps } from "./charts/StackedSteps";
import { formatChartCredits } from "./charts/shared";
import { Credits, Note, type PageSize } from "./components";
import { bucketLabels } from "./format";
import { playerBalanceDaysQuery, playerBalanceQuery, playerHistoryQuery } from "./queries";
import {
  type DayRange,
  type Drag,
  dragZoom,
  formatCredits,
  type GraphFrame,
  type GraphSettings,
  type GraphSpan,
  rangeSteps,
  SpendBars,
  SpendByTabs,
  stepFor,
  TimeGraph,
  useDaysCovering,
  zoomedSpan,
} from "./TimeGraph";

/** The balance by type over time, or what holds spent each step. */
type GraphMode = "balance" | "spend";

const SPEND_BY: readonly SpendBy[] = ["game", "bucket"];

interface GraphView {
  mode: GraphMode;
  spendBy: SpendBy;
  /** The balance's axis fits its lowest and highest points rather than running from zero. */
  zoomed: boolean;
  frame: GraphFrame;
  onSelect?: (window: HistoryWindow, scroll: boolean) => void;
  onHighlight?: (window: HistoryWindow) => void;
}

/**
 * A player's balance over time, worked back from the balance the page read. Day ranges step back
 * through earlier periods; loaded pages show only what the history list has read.
 */
export function BalanceGraph({
  userId,
  pageSize,
  settings,
  onSettingsChange,
  onSelect,
  onHighlight,
}: {
  userId: string;
  pageSize: PageSize;
  settings: GraphSettings;
  onSettingsChange: (patch: Partial<GraphSettings>) => void;
  /** The span of time a click on the graph picked, and whether the list should scroll to it. */
  onSelect?: (window: HistoryWindow, scroll: boolean) => void;
  /** The span of a spend bar a click zoomed into. */
  onHighlight?: (window: HistoryWindow) => void;
}) {
  const mode: GraphMode = settings.graphSpend ? "spend" : "balance";
  const spendBy =
    settings.spendBy && SPEND_BY.includes(settings.spendBy) ? settings.spendBy : "game";
  const zoomed = !settings.graphFromZero;
  return (
    <TimeGraph
      settings={settings}
      onSettingsChange={onSettingsChange}
      leading={
        <Tabs
          compact
          value={mode}
          options={[
            { value: "balance", label: "Balance" },
            { value: "spend", label: "Spend" },
          ]}
          onChange={next => onSettingsChange({ graphSpend: next === "spend" || undefined })}
        />
      }
      controls={
        mode === "balance" ? (
          <Tabs
            compact
            value={zoomed ? "zoomed" : "zero"}
            options={[
              { value: "zoomed", label: "Zoomed" },
              { value: "zero", label: "From zero" },
            ]}
            onChange={value => onSettingsChange({ graphFromZero: value === "zero" || undefined })}
          />
        ) : (
          <SpendByTabs
            options={SPEND_BY}
            value={spendBy}
            onChange={by => onSettingsChange({ spendBy: by === "game" ? undefined : by })}
          />
        )
      }
    >
      {frame => {
        const view: GraphView = { mode, spendBy, zoomed, frame, onSelect, onHighlight };
        return frame.range === "loaded" || !frame.period ? (
          <LoadedPagesGraph userId={userId} pageSize={pageSize} view={view} />
        ) : (
          <DaysGraph
            key={frame.range}
            userId={userId}
            range={frame.range}
            period={frame.period}
            view={view}
          />
        );
      }}
    </TimeGraph>
  );
}

/**
 * The cuts of the history pages already read at this page size, newest first, joined with `|`, as
 * far back as they run unbroken from the newest.
 */
function useLoadedCuts(userId: string, pageSize: PageSize) {
  const client = useQueryClient();
  const read = () => {
    const cuts: string[] = [""];
    for (;;) {
      const before = cuts.at(-1) || undefined;
      const page = client.getQueryData<PlayerOperationsResponse>(
        playerHistoryQuery(userId, { before, limit: pageSize }).queryKey,
      );
      if (!page?.nextBefore || cuts.includes(page.nextBefore)) return cuts.join("|");
      cuts.push(page.nextBefore);
    }
  };
  return useSyncExternalStore(listener => client.getQueryCache().subscribe(listener), read);
}

function LoadedPagesGraph({
  userId,
  pageSize,
  view,
}: {
  userId: string;
  pageSize: PageSize;
  view: GraphView;
}) {
  const balance = useQuery(playerBalanceQuery(userId));
  const cuts = useLoadedCuts(userId, pageSize).split("|");
  const pages = useQueries({
    queries: cuts.map((cut, index) => ({
      ...playerHistoryQuery(userId, { before: cut || undefined, limit: pageSize }),
      enabled: index === 0,
      staleTime: Number.POSITIVE_INFINITY,
    })),
  });
  if (balance.error) return <Problem error={balance.error} />;
  const loaded = pages.flatMap(page => (page.data ? [page.data] : []));
  if (!balance.data || loaded.length === 0) return <Note>Loading history…</Note>;
  const items = loaded.flatMap(page => page.items);
  const oldest = items.at(-1);
  if (!oldest) return <Note>No history</Note>;
  const changes = items.flatMap(item => (item.kind === "operation" ? [balanceChange(item)] : []));
  const oldestAt = Date.parse(oldest.kind === "operation" ? oldest.recordedAt : oldest.releasedAt);
  const now = Date.now();
  const reachedStart = loaded.at(-1)?.nextBefore === null;
  return (
    <PlayerChart
      view={view}
      current={balance.data.buckets}
      changes={changes}
      from={oldestAt}
      knownFrom={reachedStart ? Number.NEGATIVE_INFINITY : oldestAt}
      step={stepFor(now - oldestAt)}
      to={now}
      now={now}
      stepped={false}
      progress={null}
    />
  );
}

/** Reads the days the period needs, then works balances back. */
function DaysGraph({
  userId,
  range,
  period: { from, to },
  view,
}: {
  userId: string;
  range: DayRange;
  period: { from: number; to: number };
  view: GraphView;
}) {
  const { now, stepped, zoom } = view.frame;
  const today = new Date(now).toISOString().slice(0, 10);
  const days = useInfiniteQuery(playerBalanceDaysQuery(userId, today));
  const { read, needed, knownFrom, progress } = useDaysCovering(days, today, from, zoom);
  if (days.error) return <Problem error={days.error} />;
  const current = read[0]?.balance;
  if (!current) return <Note>Loading day 1 of {needed}…</Note>;
  return (
    <PlayerChart
      view={view}
      current={current}
      changes={read.flatMap(day => day.changes)}
      from={from}
      knownFrom={knownFrom}
      step={rangeSteps[range]}
      to={to}
      now={now}
      stepped={stepped}
      progress={progress}
    />
  );
}

interface ChartProps extends GraphSpan {
  current: BucketBalances;
  changes: readonly BalanceChange[];
}

function PlayerChart({ view, ...props }: ChartProps & { view: GraphView }) {
  const { shown, note } = zoomedSpan(props, view.frame.zoom);
  if (note) return note;
  const drag = dragZoom(view.frame.onZoom);
  const { onSelect } = view;
  const select = (scroll: boolean) =>
    onSelect && ((window: HistoryWindow) => onSelect(window, scroll));
  const { fit, onFitChange } = view.frame;
  return view.mode === "balance" ? (
    <BalanceSteps
      {...shown}
      zoomed={view.zoomed}
      fit={fit}
      onFitChange={onFitChange}
      onSelect={select(true)}
      onDrag={drag}
    />
  ) : (
    <SpendBars
      {...shown}
      fit={fit}
      onFitChange={onFitChange}
      by={view.spendBy}
      onSelect={select(false)}
      onHighlight={view.onHighlight}
      onZoom={drag}
    />
  );
}

/**
 * The balance in each type, stacked so the one spent first sits on top, stepping at each change.
 * Clicking a step picks the moment of the changes that started it; the first step has none.
 */
function BalanceSteps({
  current,
  changes,
  from,
  knownFrom,
  step,
  to,
  stepped,
  progress,
  zoomed,
  fit,
  onFitChange,
  onSelect,
  onDrag,
}: ChartProps &
  Pick<GraphFrame, "fit" | "onFitChange"> & {
    zoomed: boolean;
    onSelect?: (window: HistoryWindow) => void;
    onDrag: Drag;
  }) {
  const steps = balanceSteps(current, changes, from, to, knownFrom);
  const buckets = heldBuckets(
    steps.map(point => point.balances),
    [...SPEND_ORDER].reverse(),
  );
  const newest = steps.at(-1)?.balances;
  return (
    <StackedSteps
      title="Balance over time"
      series={buckets.map(bucket => ({ key: bucket, label: bucketLabels[bucket] }))}
      points={steps.map(point => ({
        start: new Date(point.at).toISOString(),
        values: buckets.map(bucket => creditsFromRaw(point.balances[bucket].toString())),
      }))}
      from={from}
      to={to}
      step={step}
      summary={
        <>
          {newest && <Credits raw={spendable(newest).toString()} />}{" "}
          {stepped ? "at the end of this period" : "now"}
          {progress && <span className="text-muted-foreground"> · {progress}</span>}
        </>
      }
      format={formatCredits}
      formatTick={formatChartCredits}
      zoomed={zoomed}
      fitted={fit}
      onFittedChange={onFitChange}
      onSelect={
        onSelect &&
        (point => {
          const at = steps[point]?.at ?? to;
          onSelect({ from: point === 0 ? at : at - 1, to: at });
        })
      }
      onZoom={onDrag}
    />
  );
}
